/**
 * Single React Query source for `profiles.role` (admin checks, maintenance bypass, login redirect).
 * All consumers share `["profiles", "role", userId]` → one network request while fresh.
 */
import { useQuery, type QueryKey } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { db, supabase } from "@/lib/supabase";
import { appFetch, isAppApiConfigured } from "@/lib/appApi";

function normalizeProfileRole(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  return t.length ? t : null;
}

export const PROFILE_ROLE_STALE_MS = 5 * 60 * 1000;
export const PROFILE_ROLE_GC_MS = 15 * 60 * 1000;

export function profileRoleQueryKey(userId: string | null): QueryKey {
  return ["profiles", "role", userId];
}

export async function fetchProfileRoleForUserId(userId: string): Promise<string | null> {
  // 1) Express BFF (JWT on server) — preferred when deployed
  if (isAppApiConfigured()) {
    try {
      const j = await appFetch<{ ok?: boolean; role?: string | null }>("/api/app/me/role");
      if (j && typeof j === "object" && j.ok === true) {
        const fromApi = normalizeProfileRole(j.role);
        if (fromApi != null) return fromApi;
      }
    } catch {
      /* try direct Supabase below */
    }
  }

  // 2) Direct Supabase JS client (session JWT) — bypasses flaky PostgREST BFF `/api/app/rest/v1`
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("role")
      .eq("id", userId)
      .maybeSingle();
    if (!error) {
      const fromDirect = normalizeProfileRole(data?.role);
      if (fromDirect != null) return fromDirect;
    }
  } catch {
    /* fall through */
  }

  // 3) PostgREST via dataDb (same as before)
  const { data, error } = await db
    .from("profiles")
    .select("role")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return normalizeProfileRole(data?.role);
}

/** Options for `queryClient.fetchQuery` / `prefetchQuery` (same contract as the hook). */
export function getProfileRoleFetchOptions(userId: string) {
  return {
    queryKey: profileRoleQueryKey(userId),
    queryFn: () => fetchProfileRoleForUserId(userId),
    staleTime: PROFILE_ROLE_STALE_MS,
    gcTime: PROFILE_ROLE_GC_MS,
  };
}

export function useProfileRole() {
  const userId = useAuth().user?.id ?? null;

  return useQuery({
    queryKey: profileRoleQueryKey(userId),
    queryFn: () => fetchProfileRoleForUserId(userId!),
    enabled: Boolean(userId),
    staleTime: PROFILE_ROLE_STALE_MS,
    gcTime: PROFILE_ROLE_GC_MS,
    refetchOnWindowFocus: false,
  });
}
